import { defineStore } from 'pinia';
import authService from '@/services/authService';
import { trackRegistration } from '@/utils/acquisition';
import googleAuthService from '@/services/googleAuthService';
import appleAuthService from '@/services/appleAuthService';
import {
  identifyChatwootUser,
  resetChatwoot,
} from '@/utils/chatwoot';

// Concurrent session refreshes share one request and one identity update.
let verifySessionInFlight = null;

function readCachedUser() {
  try {
    const user = JSON.parse(localStorage.getItem('indiefy-user'));
    return user?._id ? user : null;
  } catch {
    return null;
  }
}

export const useAuthStore = defineStore({
  id: 'auth',

  state: () => {
    const user = readCachedUser();
    return {
      user,
      isAuthenticated: !!user,
      loading: false,
      initializing: true,
      sessionVerified: false,
      error: null,
      requiresTwoFactor: false,
      captchaData: null,
    };
  },

  getters: {
    currentUser: (state) => state.user,
    isLoading: (state) => state.loading,
    isInitializing: (state) => state.initializing,
    authError: (state) => state.error,
    needsTwoFactor: (state) => state.requiresTwoFactor,
  },

  actions: {
    // Reddit Pixel advanced matching: re-init with user identifiers
    // once a session is established (improves conversion attribution)
    identifyRedditPixel(user) {
      if (!window.rdt || !user) return;

      const matchData = {};

      if (user.email) {
        matchData.email = user.email;
      }
      if (user._id) {
        matchData.externalId = user._id;
      }
      if (user.phone_number) {
        matchData.phoneNumber = `${user.phone_code || ''}${user.phone_number}`;
      }

      if (Object.keys(matchData).length > 0) {
        window.rdt('init', 'a2_jdlk2bcrqt1m', matchData);
      }
    },

    async getCaptcha() {
      this.loading = true;
      this.error = null;

      try {
        const captchaResponse = await authService.getCaptcha();
        this.captchaData = captchaResponse;
        return captchaResponse;
      } catch (error) {
        this.error = error.response?.data?.message || 'Failed to load captcha';
        return null;
      } finally {
        this.loading = false;
      }
    },

    async login(username, password, captcha, lang = 'en') {
      this.loading = true;
      this.error = null;
      this.requiresTwoFactor = false;

      try {
        const response = await authService.login({
          username,
          password,
          captcha,
          lang,
        });

        // Check if two-factor auth is required
        if (response.twofactor) {
          this.requiresTwoFactor = true;
          return { requiresTwoFactor: true };
        }

        // Normal authentication success
        this.user = response;
        this.isAuthenticated = true;

        // Check if user is blocked after login
        if (this.isUserBlocked(response)) {
          // Save to localStorage so we can show the reason
          localStorage.setItem('indiefy-user', JSON.stringify(response));
          return { blocked: true };
        }

        // Save to localStorage
        localStorage.setItem('indiefy-user', JSON.stringify(response));

        this.identifyRedditPixel(this.user);
        identifyChatwootUser(this.user);

        return { success: true };
      } catch (error) {
        this.error = error.response?.data?.name || 'Authentication failed';
        return { error: this.error };
      } finally {
        this.loading = false;
      }
    },

    async confirmTwoFactor(code) {
      this.loading = true;
      this.error = null;

      try {
        const response = await authService.confirmTwoFactor({ code });

        this.user = response;
        this.isAuthenticated = true;
        this.requiresTwoFactor = false;

        // Check if user is blocked after two-factor confirmation
        if (this.isUserBlocked(response)) {
          // Save to localStorage so we can show the reason
          localStorage.setItem('indiefy-user', JSON.stringify(response));
          return { blocked: true };
        }

        // Save to localStorage
        localStorage.setItem('indiefy-user', JSON.stringify(response));

        this.identifyRedditPixel(this.user);
        identifyChatwootUser(this.user);

        return { success: true };
      } catch (error) {
        const serverMessage = error.response?.data?.message;
        const hasMessage = typeof serverMessage === 'string' && serverMessage.trim();
        this.error = hasMessage ? serverMessage : 'Two-factor authentication failed';
        let errorKey;
        if (['ECONNABORTED', 'ETIMEDOUT'].includes(error.code)) {
          errorKey = 'auth.two-factor-timeout';
        } else if (error.response?.status === 429) {
          errorKey = 'auth.two-factor-rate-limit';
        } else if (!error.response && (error.request || error.code === 'ERR_NETWORK')) {
          errorKey = 'auth.two-factor-network';
        } else if (!hasMessage) {
          errorKey = 'auth.two-factor-failed';
        }
        return { error: this.error, errorKey };
      } finally {
        this.loading = false;
      }
    },

    async register(userData) {
      this.loading = true;
      this.error = null;

      try {
        if (localStorage.getItem('referral')) {
          userData.referred_by_user = localStorage.getItem('referral');
          localStorage.removeItem('referral');
        }

        const response = await authService.register(userData);

        this.user = response;
        this.isAuthenticated = true;

        trackRegistration(this.user, 'email');

        // Save to localStorage
        localStorage.setItem('indiefy-user', JSON.stringify(response));

        this.identifyRedditPixel(this.user);
        identifyChatwootUser(this.user);

        return { success: true, firstTime: response.first_time === true };
      } catch (error) {
        this.error = error.response?.data?.message || 'Registration failed';
        return { error: this.error };
      } finally {
        this.loading = false;
      }
    },

    async logout() {
      this.loading = true;

      try {
        await authService.logout();
        // Use the clearSession helper
        this.clearSession();
        return { success: true };
      } catch (error) {
        // Even if server logout fails, clear the local session
        this.clearSession();
        this.error = error.response?.data?.message || 'Logout failed';
        return { success: true }; // Still return success to ensure UI updates
      } finally {
        this.loading = false;
      }
    },

    // Asynchronous verification with the server
    async verifySession() {
      if (verifySessionInFlight) {
        return verifySessionInFlight;
      }

      const run = async () => {
        // Don't set loading state to avoid UI flickering
        try {
          const userData = await authService.getCurrentUser();
          // Update user data with latest from server
          if (userData) {
            this.sessionVerified = true;
            const serialized = JSON.stringify(userData);
            // Skip reactive reassignment when the server returned the same
            // payload we already have cached. Reassigning `this.user` to a
            // new object reference on every page load needlessly fires every
            // watcher on `currentUser` across the app (theme enforcement,
            // Apple-relay modal check, navigation bar computeds, etc.),
            // which can manifest as a visible "blink" on initial load.
            const unchanged = serialized ===
              localStorage.getItem('indiefy-user');

            // Check if user is blocked/banned/frozen
            if (this.isUserBlocked(userData)) {
              if (!unchanged) {
                this.user = userData;
                localStorage.setItem('indiefy-user', serialized);
              }
              this.isAuthenticated = true;

              // Import router dynamically to avoid circular dependency
              const router = await import('@/router');
              router.default.push('/account/blocked');
              return false;
            }

            if (!unchanged) {
              this.user = userData;
              localStorage.setItem('indiefy-user', serialized);
            }
            this.isAuthenticated = true;

            this.identifyRedditPixel(userData);
            identifyChatwootUser(userData);

            return true;
          } else {
            console.warn('Session verification returned no user data');
            this.clearSession();
            return false;
          }
        } catch (apiError) {
          this.sessionVerified = false;
          console.warn('Session verification API call failed:', apiError);
          // Only clear session for auth errors (401)
          if (apiError.response && apiError.response.status === 401) {
            this.clearSession();
          }
          return false;
        } finally {
          this.initializing = false;
        }
      };

      verifySessionInFlight = run();
      try {
        return await verifySessionInFlight;
      } finally {
        verifySessionInFlight = null;
      }
    },

    // Helper method to check if user is blocked
    isUserBlocked(user = null) {
      const userData = user || this.user;
      if (!userData) return false;

      // Check if freeze or banned properties exist and are truthy
      // Handle both string and number values
      const isFrozen = userData.freeze;
      const isBanned = userData.banned;
      const isBlocked = isFrozen || isBanned;

      return isBlocked;
    },

    clearSession() {
      // Helper method to clear user session state
      this.sessionVerified = false;
      this.user = null;
      this.isAuthenticated = false;
      localStorage.removeItem('indiefy-user');
      resetChatwoot();
    },

    clearError() {
      this.error = null;
    },

    async getUserActivity(params) {
      this.loading = true;
      this.error = null;

      try {
        const activity = await authService.getUserActivity(params);
        return activity;
      } catch (error) {
        this.error = error.response?.data?.message || 'Failed to fetch user activity';
        return null;
      } finally {
        this.loading = false;
      }
    },

    async loginWithGoogle() {
      this.loading = true;
      this.error = null;
      this.requiresTwoFactor = false;

      try {
        // Get Google user data
        const googleUserData = await googleAuthService.signInWithGoogle();

        // Add language and source
        const userData = {
          ...googleUserData,
          lang: this.getUserLanguage(),
          source: 'web',
        };

        if (localStorage.getItem('referral')) {
          userData.referred_by_user = localStorage.getItem('referral');
          localStorage.removeItem('referral');
        }

        if (localStorage.getItem('utm_campaign')) {
          userData.utm_campaign = localStorage.getItem('utm_campaign');
        }

        // Send to backend
        const response = await authService.googleLogin(userData);


        // Check if two-factor auth is required
        if (response.twofactor) {
          this.requiresTwoFactor = true;
          return { requiresTwoFactor: true };
        }

        // Normal authentication success
        this.user = response;
        this.isAuthenticated = true;

        // Check if user is blocked after login
        if (this.isUserBlocked(response)) {
          // Save to localStorage so we can show the reason
          localStorage.setItem('indiefy-user', JSON.stringify(response));
          return { blocked: true };
        }

        trackRegistration(this.user, 'google');

        // Save to localStorage
        localStorage.setItem('indiefy-user', JSON.stringify(response));

        this.identifyRedditPixel(this.user);

        window.zE && window.zE('webWidget', 'identify', {
          name: this.user.name || this.user.username,
          email: this.user.email,
        });

        identifyChatwootUser(this.user);

        return { success: true, firstTime: response.first_time === true };
      } catch (error) {
        console.error('Google login error:', error);

        // Handle specific error cases
        if (error.response?.data?.name === 'duplicated_email') {
          this.error = 'This email is already registered. Please try logging in instead.';
        } else if (error.response?.data?.name === 'deactivated_account') {
          this.error = 'This account has been deactivated. Please contact support.';
        } else {
          this.error = error.response?.data?.message || 'Google authentication failed';
        }

        return { error: this.error };
      } finally {
        this.loading = false;
      }
    },

    async loginWithApple() {
      this.loading = true;
      this.error = null;
      this.requiresTwoFactor = false;

      try {
        // Get Apple user data
        const appleUserData = await appleAuthService.signInWithApple();

        // Add language and source
        const userData = {
          ...appleUserData,
          lang: this.getUserLanguage(),
          source: 'web',
        };

        if (localStorage.getItem('referral')) {
          userData.referred_by_user = localStorage.getItem('referral');
          localStorage.removeItem('referral');
        }

        if (localStorage.getItem('utm_campaign')) {
          userData.utm_campaign = localStorage.getItem('utm_campaign');
        }

        // Send to backend
        const response = await authService.appleLogin(userData);


        trackRegistration(response, 'apple');

        // Check if two-factor auth is required
        if (response.twofactor) {
          this.requiresTwoFactor = true;
          return { requiresTwoFactor: true };
        }

        // Normal authentication success
        this.user = response;
        this.isAuthenticated = true;

        // Check if user is blocked after login
        if (this.isUserBlocked(response)) {
          // Save to localStorage so we can show the reason
          localStorage.setItem('indiefy-user', JSON.stringify(response));
          return { blocked: true };
        }

        // Save to localStorage
        localStorage.setItem('indiefy-user', JSON.stringify(response));

        this.identifyRedditPixel(this.user);

        window.zE && window.zE('webWidget', 'identify', {
          name: this.user.name || this.user.username,
          email: this.user.email,
        });

        identifyChatwootUser(this.user);

        return { success: true, firstTime: response.first_time === true };
      } catch (error) {
        console.error('Apple login error:', error);

        // Handle specific error cases
        if (error.response?.data?.name === 'duplicated_email') {
          this.error = 'This email is already registered. Please try logging in instead.';
        } else if (error.response?.data?.name === 'deactivated_account') {
          this.error = 'This account has been deactivated. Please contact support.';
        } else {
          this.error = error.response?.data?.message || 'Apple authentication failed';
        }

        return { error: this.error };
      } finally {
        this.loading = false;
      }
    },

    // Helper to get user language
    getUserLanguage() {
      // Try to get from i18n if available
      if (window.i18n && window.i18n.global && window.i18n.global.locale) {
        return window.i18n.global.locale.value;
      }

      // Fallback to browser language
      return navigator.language.split('-')[0] || 'en';
    },

    async handleAppleCallback(callbackData) {
      this.loading = true;
      this.error = null;
      this.requiresTwoFactor = false;

      try {
        // Send the authorization code to the backend
        const response = await authService.appleCallback(callbackData);

        // Check if two-factor auth is required
        if (response.twofactor) {
          this.requiresTwoFactor = true;
          return { requiresTwoFactor: true };
        }

        // Normal authentication success
        this.user = response;
        this.isAuthenticated = true;

        // Check if user is blocked after login
        if (this.isUserBlocked(response)) {
          // Save to localStorage so we can show the reason
          localStorage.setItem('indiefy-user', JSON.stringify(response));
          return { blocked: true };
        }

        // Save to localStorage
        localStorage.setItem('indiefy-user', JSON.stringify(response));

        this.identifyRedditPixel(this.user);
        identifyChatwootUser(this.user);

        return { success: true };
      } catch (error) {
        console.error('Apple callback error:', error);

        // Handle specific error cases
        if (error.response?.data?.name === 'duplicated_email') {
          this.error = 'This email is already registered. Please try logging in instead.';
        } else if (error.response?.data?.name === 'deactivated_account') {
          this.error = 'This account has been deactivated. Please contact support.';
        } else {
          this.error = error.response?.data?.message || 'Apple authentication failed';
        }

        return { error: this.error };
      } finally {
        this.loading = false;
      }
    },
  },
});
